/**
 * Custom TipTap extension for Tab/Shift-Tab paragraph and heading indentation.
 *
 * Adds an `indent` attribute (0–8) to paragraph and heading nodes.
 * Tab increases indent; Shift-Tab decreases it. Skips when inside
 * lists (which already handle Tab for nesting).
 *
 * Closes #181
 */

import { Extension } from '@tiptap/core';
import type { EditorState, PluginKey } from '@tiptap/pm/state';
import { MentionPluginKey } from './MentionExtension';
import { TagPluginKey } from './TagExtension';
import { EntityLinkPluginKey } from './EntityLinkExtension';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    indent: {
      indent: () => ReturnType;
      outdent: () => ReturnType;
    };
  }
}

const MAX_INDENT = 8;

// The suggestion popups (`@` mention, `#` tag, `[[` entity link) each store their
// open/closed state under their own plugin key. When one is open it must own Tab
// (to select the highlighted item — see SuggestionList.onKeyDown), so the Indent
// keymap has to yield instead of indenting the paragraph underneath the popup.
const SUGGESTION_KEYS: PluginKey[] = [MentionPluginKey, TagPluginKey, EntityLinkPluginKey];

export function isSuggestionActive(state: EditorState): boolean {
  return SUGGESTION_KEYS.some((key) => {
    const pluginState = key.getState(state) as { active?: boolean } | undefined;
    return pluginState?.active === true;
  });
}

export const Indent = Extension.create({
  name: 'indent',

  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'heading'],
        attributes: {
          indent: {
            default: 0,
            parseHTML: (element) => {
              const value = element.getAttribute('data-indent');
              return value ? parseInt(value, 10) : 0;
            },
            renderHTML: (attributes) => {
              if (!attributes.indent || attributes.indent === 0) return {};
              return { 'data-indent': attributes.indent };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      indent:
        () =>
        ({ tr, state }) => {
          const { selection } = state;
          let changed = false;

          state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
            if (node.type.name === 'paragraph' || node.type.name === 'heading') {
              const current = (node.attrs.indent as number) || 0;
              if (current < MAX_INDENT) {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  indent: current + 1,
                });
                changed = true;
              }
            }
          });

          return changed;
        },

      outdent:
        () =>
        ({ tr, state }) => {
          const { selection } = state;
          let changed = false;

          state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
            if (node.type.name === 'paragraph' || node.type.name === 'heading') {
              const current = (node.attrs.indent as number) || 0;
              if (current > 0) {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  indent: current - 1,
                });
                changed = true;
              }
            }
          });

          return changed;
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      Tab: () => {
        // A suggestion popup is open — yield Tab so it can select the highlighted item.
        if (isSuggestionActive(this.editor.state)) return false;
        // Don't handle Tab when inside a list — let the list extension handle it
        const { $from } = this.editor.state.selection;
        for (let depth = $from.depth; depth > 0; depth--) {
          const node = $from.node(depth);
          if (node.type.name === 'listItem' || node.type.name === 'taskItem') {
            return false;
          }
        }
        return this.editor.commands.indent();
      },
      'Shift-Tab': () => {
        // A suggestion popup is open — yield so it (not the editor) owns the key.
        if (isSuggestionActive(this.editor.state)) return false;
        const { $from } = this.editor.state.selection;
        for (let depth = $from.depth; depth > 0; depth--) {
          const node = $from.node(depth);
          if (node.type.name === 'listItem' || node.type.name === 'taskItem') {
            return false;
          }
        }
        return this.editor.commands.outdent();
      },
    };
  },
});
